import {useEffect, useRef, useState} from 'react';
import {claveDeTexto, tr, trx} from '../i18n/idioma';
import {columnasPorDefecto, type DefinicionDeColumnas} from './columnas';

type Props<T, O extends string> = {
  definicion: DefinicionDeColumnas<T, O>;
  visibles: string[];
  onCambiar: (ids: string[]) => void;
  // Para mostrarlo como opcion de una lista (Herramientas).
  claseDelBoton?: string;
};

// Lista desplegable para elegir que columnas se ven y en que orden. Una
// columna nueva se agrega al final; el orden tambien se cambia arrastrando
// los encabezados de la tabla. La columna fija (la llave) no se puede ocultar.
export function SelectorDeColumnas<T, O extends string>({definicion, visibles, onCambiar, claseDelBoton}: Props<T, O>) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const cerrar = (evento: MouseEvent) => {
      if (caja.current && !caja.current.contains(evento.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', cerrar);
    return () => document.removeEventListener('mousedown', cerrar);
  }, [abierto]);

  const alternar = (id: string) => {
    if (id === definicion.fija) return;
    onCambiar(visibles.includes(id) ? visibles.filter(v => v !== id) : [...visibles, id]);
  };

  const mover = (id: string, paso: -1 | 1) => {
    const indice = visibles.indexOf(id);
    const nuevo = indice + paso;
    if (indice < 0 || nuevo < 0 || nuevo >= visibles.length) return;
    const copia = [...visibles];
    copia.splice(indice, 1);
    copia.splice(nuevo, 0, id);
    onCambiar(copia);
  };

  const titulo = (id: string) => {
    const columna = definicion.columnas.find(c => c.id === id);
    return columna ? trx(columna.titulo) : id;
  };

  return (
    <div ref={caja} className="relative">
      <button
        type="button"
        onClick={() => setAbierto(a => !a)}
        className={claseDelBoton ?? 'rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel'}
        aria-expanded={abierto}
      >
        {tr('Columnas', 'Columns')} ({visibles.length})
      </button>
      {abierto && (
        <div className="absolute right-0 z-20 mt-1 flex max-h-[32rem] w-[40rem] max-w-[calc(100vw-4rem)] overflow-hidden rounded-lg border border-linea bg-white shadow-lg">
          <div className="w-1/2 overflow-auto border-r border-linea p-3">
            <div className="flex items-center justify-between pb-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-apagado">
                {tr('Disponibles', 'Available')}
              </span>
              <button
                type="button"
                className="text-xs text-verde hover:underline"
                onClick={() => onCambiar(columnasPorDefecto(definicion))}
              >
                {tr('Volver a las de siempre', 'Back to the defaults')}
              </button>
            </div>
            {definicion.grupos.map(grupo => (
              <div key={claveDeTexto(grupo)} className="mt-2">
                <div className="text-xs font-medium text-apagado">{trx(grupo)}</div>
                <div className="mt-1">
                  {definicion.columnas
                    .filter(c => claveDeTexto(c.grupo) === claveDeTexto(grupo))
                    .map(columna => (
                      <label key={columna.id} className="flex cursor-pointer items-center gap-2 py-0.5 text-sm">
                        <input
                          type="checkbox"
                          checked={visibles.includes(columna.id)}
                          disabled={columna.id === definicion.fija}
                          onChange={() => alternar(columna.id)}
                          className="accent-verde"
                        />
                        <span className="truncate">{trx(columna.titulo)}</span>
                      </label>
                    ))}
                </div>
              </div>
            ))}
          </div>
          <div className="w-1/2 overflow-auto p-3">
            <div className="pb-2 text-xs font-semibold uppercase tracking-wide text-apagado">
              {tr('Orden en la tabla', 'Order in the table')}
            </div>
            <ol className="space-y-0.5">
              {visibles.map((id, indice) => (
                <li key={id} className="flex items-center gap-1 text-sm">
                  <span className="w-5 text-right text-xs text-apagado">{indice + 1}.</span>
                  <span className="flex-1 truncate">{titulo(id)}</span>
                  <button
                    type="button"
                    onClick={() => mover(id, -1)}
                    disabled={indice === 0}
                    className="rounded px-1 text-xs text-apagado hover:bg-papel disabled:opacity-30"
                    aria-label={tr(`Subir ${titulo(id)}`, `Move ${titulo(id)} up`)}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => mover(id, 1)}
                    disabled={indice === visibles.length - 1}
                    className="rounded px-1 text-xs text-apagado hover:bg-papel disabled:opacity-30"
                    aria-label={tr(`Bajar ${titulo(id)}`, `Move ${titulo(id)} down`)}
                  >
                    ▼
                  </button>
                  {id !== definicion.fija && (
                    <button
                      type="button"
                      onClick={() => alternar(id)}
                      className="rounded px-1 text-xs text-apagado hover:bg-papel"
                      aria-label={tr(`Quitar ${titulo(id)}`, `Remove ${titulo(id)}`)}
                    >
                      ✕
                    </button>
                  )}
                </li>
              ))}
            </ol>
            <p className="mt-3 text-xs text-apagado">
              {tr(
                'También puedes arrastrar los encabezados de la tabla. Lo que elijas queda guardado en tu usuario y te sigue a cualquier computador.',
                'You can also drag the table headers. Your choice is saved in your user and follows you to any computer.',
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
